/**
 *
 * UserDetails
 *
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Descriptions, Dropdown, Menu, PageHeader, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';

import * as S from './UserDetails.styles';
import { RouteConstants } from 'app/routes';
import {
  selectSelectedEnv,
  selectSelectedEnvId,
} from 'app/containers/HiikoEnv/slice/selectors';
import { useUserSlice } from '../../slice';
import { TextSemibold16 } from 'styles/typography';
import { selectUserDetailsState } from '../../slice/selectors';
import { IUserCompaniesListItem, PermissionsEnum } from '../../slice/types';
import UserCompanyPermissionsForm from '../../parts/UserCompanyPermissionsForm/UserCompanyPermissionsForm';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { EllipsisIcon } from 'app/components/CommonIcons/CommonIcons';
import { StyledTable } from 'app/components/StyledTable/StyledTable';

interface IReactRouterParams {
  userId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const CompanyDetails = ({
  history,
  match: {
    params: { userId },
  },
}: IProps) => {
  const { t } = useTranslation();

  const { actions } = useUserSlice();

  const dispatch = useDispatch();

  const selectedEnv = useSelector(selectSelectedEnv);
  const { loading, user, list } = useSelector(selectUserDetailsState);
  const envId = useSelector(selectSelectedEnvId);

  useEffect(() => {
    if (userId && envId) {
      dispatch(
        actions.getUserDetails({
          envId,
          userId: Number(userId),
        }),
      );
    }
  }, [userId, actions, dispatch, envId]);

  const columns = [
    {
      title: t('User.Details.Table.Company', 'Company'),
      dataIndex: 'name',
      key: 'company',
      width: '35%',
      sorter: {
        compare: (a: IUserCompaniesListItem, b: IUserCompaniesListItem) => {
          return a.name.localeCompare(b.name);
        },
      },
    },
    {
      title: t(
        'User.Details.Table.EnvironmentOwnerEmail',
        'Environment owner email',
      ),
      dataIndex: 'hiikoEnvironmentOwnerEmail',
      key: 'hiikoEnvironmentOwnerEmail',
      render: (hiikoEnvironmentOwnerEmail: string) =>
        hiikoEnvironmentOwnerEmail,
      width: '30%',
    },

    {
      title: t('User.Details.Table.ActivePermissions', 'Active permissions'),
      dataIndex: 'permissionsCount',
      key: 'permissionsCount',
      render: (_: unknown, userCompaniesItem: IUserCompaniesListItem) => {
        let activePermissionsCount = 0;

        Object.entries(userCompaniesItem.userCompanyPermissions).forEach(
          ([_, value]) => {
            value && activePermissionsCount++;
          },
        );
        return activePermissionsCount;
      },
      width: '25%',
    },
    {
      title: t('Common.Actions', 'Actions'),
      dataIndex: 'actions',
      key: 'actions',
      render: (_: unknown, userCompany: IUserCompaniesListItem) => (
        <Dropdown
          trigger={['click']}
          overlay={
            <PermissionsWrapper
              requiredEditPermissions={[PermissionsEnum.usersAdministration]}
            >
              <Menu>
                <Menu.Item
                  onClick={() =>
                    dispatch(
                      actions.toggleAddUserCompanyForm({
                        isOpen: true,
                        permissions: userCompany.userCompanyPermissions,
                        type: 'edit',
                        companyId: userCompany.id,
                        companyName: userCompany.name,
                      }),
                    )
                  }
                >
                  {t(
                    'User.Details.Table.ManagePermissions',
                    'Manage permissions',
                  )}
                </Menu.Item>
              </Menu>
            </PermissionsWrapper>
          }
        >
          <EllipsisIcon />
        </Dropdown>
      ),
      width: '10%',
    },
  ];

  if (!selectedEnv || !user) {
    return null;
  }
  return (
    <S.UserDetailsWrapper>
      {loading ? (
        <S.Spinner size="large" />
      ) : (
        <>
          <PageHeader
            onBack={() =>
              history.push(
                generatePath(RouteConstants.user.list, {
                  envId: selectedEnv.id,
                }),
              )
            }
            className="page-header"
            title={t('User.Details.TitleUser', 'User')}
            // extra={[
            //   <Button
            //     key="1"
            //     // type="secon"
            //     onClick={() => console.log('edit clicked')}
            //   >
            //     {t('User.Details.Edit', 'Edit data')}
            //   </Button>,
            // ]}
          />
          <S.DetailsWrapper>
            <Descriptions
              bordered
              column={2}
              title={
                user.name && user.lastName
                  ? `${user.name} ${user.lastName}`
                  : t('User.Details.UserInvited', 'Invited user')
              }
            >
              <Descriptions.Item
                label={t('User.Details.EmailAddress', 'Email address')}
              >
                {user.email}
              </Descriptions.Item>

              <Descriptions.Item
                label={t('User.Details.RegistrationDate', 'Registration date')}
              >
                {user.registrationDate}
              </Descriptions.Item>
            </Descriptions>
            <Row
              align="middle"
              justify="space-between"
              style={{
                margin: '61px 0 16px 0',
              }}
            >
              <TextSemibold16>
                {t(
                  'User.Details.CompanyInOtherEnvironments',
                  'Company in other environments',
                )}
              </TextSemibold16>
              <PermissionsWrapper
                requiredEditPermissions={[PermissionsEnum.usersAdministration]}
              >
                <Button
                  type="primary"
                  onClick={() =>
                    dispatch(
                      actions.toggleAddUserCompanyForm({
                        isOpen: true,
                        type: 'add',
                      }),
                    )
                  }
                >
                  {t('User.Details.DesignateCompany', 'Designate a company')}
                </Button>
              </PermissionsWrapper>
            </Row>

            <StyledTable
              rowKey="id"
              dataSource={list}
              columns={columns}
              pagination={false}
            />
          </S.DetailsWrapper>
        </>
      )}
      <UserCompanyPermissionsForm email={user.email} userId={user.id} />
    </S.UserDetailsWrapper>
  );
};

export default CompanyDetails;
